/** Read persona fields from API payloads (snake_case or camelCase). */
export function personaField(obj, camel, snake) {
	if (!obj) return "";
	const value = obj[camel] ?? obj[snake];
	return value == null ? "" : String(value).trim();
}

/** Normalize persona object from llm_input_payload or run metadata. */
export function normalizePersonaRecord(raw) {
	if (!raw || typeof raw !== "object") return null;
	const name = personaField(raw, "name", "name");
	const profession = personaField(raw, "profession", "profession");
	const gender = personaField(raw, "gender", "gender");
	const ageRange = personaField(raw, "ageRange", "age_range");
	const location = personaField(raw, "location", "location");
	const language = personaField(raw, "language", "language");
	const tone = personaField(raw, "tone", "tone");
	const likes = personaField(raw, "likes", "likes");
	const dislikes = personaField(raw, "dislikes", "dislikes");
	const interests = personaField(raw, "interests", "interests");
	const notes = personaField(raw, "notes", "notes");
	const id = raw.id ?? raw.persona_id ?? raw.personaId;

	const hasContent =
		name ||
		profession ||
		gender ||
		ageRange ||
		location ||
		language ||
		tone ||
		likes ||
		dislikes ||
		interests ||
		notes ||
		id != null;

	if (!hasContent) return null;

	return {
		id,
		name,
		profession,
		gender,
		ageRange,
		location,
		language,
		tone,
		likes,
		dislikes,
		interests,
		notes,
	};
}

export function writingRunPersonaId(run) {
	if (!run || typeof run !== "object") return null;
	const raw =
		run.persona_id ??
		run.personaId ??
		run.writing_persona_id ??
		run.writingPersonaId ??
		run.persona?.id ??
		run.persona?.persona_id ??
		run.persona?.personaId;
	if (raw == null || raw === "") return null;
	const id = Number(raw);
	return Number.isFinite(id) ? id : null;
}

export function personaFromWritingRun(run) {
	if (!run || typeof run !== "object") return null;

	const payloadPersona = run?.result?.recommendation?.llm_input_payload?.persona;
	const embedded = run.persona ?? run.writing_persona ?? run.writingPersona;
	const fromPayload = normalizePersonaRecord(payloadPersona);
	const fromEmbedded = normalizePersonaRecord(embedded);

	const name =
		fromPayload?.name ||
		fromEmbedded?.name ||
		personaField(run, "personaName", "persona_name");
	const profession =
		fromPayload?.profession ||
		fromEmbedded?.profession ||
		personaField(run, "personaProfession", "persona_profession");
	const payloadId = fromPayload?.id ?? fromEmbedded?.id;
	const id = writingRunPersonaId(run) ?? payloadId;

	if (fromPayload || fromEmbedded) {
		return normalizePersonaRecord({
			...(fromEmbedded || {}),
			...(fromPayload || {}),
			name: name || fromPayload?.name || fromEmbedded?.name,
			profession: profession || fromPayload?.profession || fromEmbedded?.profession,
			id: id ?? fromPayload?.id ?? fromEmbedded?.id,
		});
	}

	if (!name && (id == null || id === "" || Number(id) === 0)) return null;

	return normalizePersonaRecord({
		id,
		name,
		profession,
		tone: personaField(run, "personaTone", "persona_tone"),
	});
}

/** Whether a history row should show persona (matches analysis history rules). */
export function hasWritingRunPersona(run) {
	const id = writingRunPersonaId(run);
	if (id != null && id !== 0) return true;
	const persona = personaFromWritingRun(run);
	return Boolean(persona?.name || persona?.profession);
}

/**
 * Persona label for history lists — same pattern as analysis history, with catalog lookup.
 * @param {Map<number, object>} [personaById]
 */
export function writingRunPersonaHistoryLabel(run, personaById) {
	const id = writingRunPersonaId(run);
	const fromRun = personaFromWritingRun(run);
	const fromCatalog = id != null && id !== 0 ? personaById?.get(id) : null;

	const catalogName = fromCatalog ? personaField(fromCatalog, "name", "name") : "";
	const name =
		run?.persona_name ??
		run?.personaName ??
		fromRun?.name ??
		catalogName ??
		null;

	if (id != null && id !== 0) {
		const base = name || `#${id}`;
		const profession =
			fromRun?.profession ||
			personaField(fromCatalog, "profession", "profession") ||
			personaField(run, "personaProfession", "persona_profession");
		if (profession && profession !== name) {
			return `${base} · ${profession}`;
		}
		return base;
	}

	if (name) {
		const profession = fromRun?.profession;
		return profession && profession !== name ? `${name} · ${profession}` : name;
	}

	return fromRun?.profession || null;
}

/** Fields to merge onto list rows after a full run load (payload often has persona). */
export function writingRunPersonaHistoryHints(run) {
	if (!run || run.id == null) return null;
	const persona = personaFromWritingRun(run);
	const id = writingRunPersonaId(run) ?? persona?.id;
	if ((id == null || id === 0) && !persona?.name && !persona?.profession) {
		return null;
	}
	const payloadPersona = run?.result?.recommendation?.llm_input_payload?.persona;
	return {
		persona_id: id,
		personaId: id,
		persona_name: persona?.name || undefined,
		personaName: persona?.name || undefined,
		persona_profession: persona?.profession || undefined,
		personaProfession: persona?.profession || undefined,
		persona: payloadPersona ?? run.persona ?? run.writing_persona ?? run.writingPersona,
	};
}

/** @deprecated use writingRunPersonaHistoryLabel */
export function writingRunPersonaHistoryLine(run, personaById) {
	return writingRunPersonaHistoryLabel(run, personaById);
}

/** Ordered labeled rows for context panel (skips empty values). */
export function personaContextDetailRows(persona) {
	const p = normalizePersonaRecord(persona);
	if (!p) return [];

	const rows = [
		{ key: "profession", label: "Profession", value: p.profession },
		{ key: "tone", label: "Tone", value: p.tone },
		{ key: "gender", label: "Sex", value: p.gender },
		{ key: "age", label: "Age range", value: p.ageRange },
		{ key: "location", label: "Location", value: p.location },
		{ key: "language", label: "Language", value: p.language },
		{ key: "likes", label: "Likes", value: p.likes },
		{ key: "dislikes", label: "Dislikes", value: p.dislikes },
		{ key: "interests", label: "Interests", value: p.interests },
		{ key: "notes", label: "Notes", value: p.notes },
	];

	return rows.filter((row) => row.value);
}

export function personaContextMetaLine(persona) {
	const p = normalizePersonaRecord(persona);
	if (!p) return "";

	return [p.gender, p.ageRange, p.location, p.language, p.tone].filter(Boolean).join(" • ");
}
